import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import {
  getAdminConversationReports,
  updateConversationReport,
} from "../../services/conversationReportsService";

const STATUS_OPTIONS = ["pending", "in_review", "resolved", "dismissed"];
const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  in_review: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
  dismissed: "bg-gray-100 text-gray-700",
};

const formatLabel = (value) =>
  String(value || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const userName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Unknown User";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

export const ConversationReportsSection = ({ showMessage }) => {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [nextStatus, setNextStatus] = useState("in_review");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await getAdminConversationReports({ status, limit: 100 });
      setReports(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      console.error("Failed to load conversation reports:", error);
      setReports([]);
      showMessage({
        message: error?.message || "Failed to load conversation reports",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // The selected status is the only input used to reload the queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const openReport = (report) => {
    setSelectedReport(report);
    setNextStatus(report.status === "pending" ? "in_review" : report.status);
    setResolutionNotes(report.resolutionNotes || "");
  };

  const closeReport = () => {
    if (updating) return;
    setSelectedReport(null);
    setResolutionNotes("");
  };

  const saveReview = async () => {
    if (!selectedReport?.id || updating) return;
    setUpdating(true);
    try {
      const response = await updateConversationReport(selectedReport.id, {
        status: nextStatus,
        resolutionNotes: resolutionNotes.trim() || undefined,
      });
      showMessage({
        message: response?.message || "Conversation report updated",
        type: "success",
      });
      setSelectedReport(null);
      setResolutionNotes("");
      await loadReports();
    } catch (error) {
      console.error("Failed to update conversation report:", error);
      showMessage({
        message: error?.message || "Failed to update conversation report",
        type: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Conversation Reports</h2>
            <p className="mt-1 text-sm text-gray-500">
              Review safety and conduct reports submitted from message threads.
            </p>
          </div>
          <select
            value={status}
            onChange={({ target }) => setStatus(target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-medium-blue focus:outline-none focus:ring-2 focus:ring-brand-medium-blue"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {formatLabel(option)}
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-medium-blue" />
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white py-10 text-center text-gray-500">
            No {formatLabel(status).toLowerCase()} conversation reports.
          </div>
        )}

        {!loading && reports.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-brand-light-blue">
                <tr>
                  {["Reporter", "Reported User", "Reason", "Submitted", "Status", ""].map(
                    (header, index) => (
                      <th
                        key={header || `actions-${index}`}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-brand-dark-blue"
                      >
                        {header}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-brand-light-blue/40">
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="font-medium text-gray-900">{userName(report.reportingUser)}</div>
                      <div className="text-xs text-gray-500">{report.reportingUser?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="font-medium text-gray-900">{userName(report.reportedUser)}</div>
                      <div className="text-xs text-gray-500">{report.reportedUser?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatLabel(report.reason)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(report.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[report.status]}`}>
                        {formatLabel(report.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openReport(report)}
                        className="text-sm font-semibold text-brand-medium-blue hover:underline"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={Boolean(selectedReport)} onClose={closeReport} maxWidth="md" fullWidth>
        <DialogTitle>Conversation Report #{selectedReport?.id}</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <div className="space-y-5 pt-2 text-sm text-gray-700">
              <div className="grid gap-3 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
                <div><strong>Reporter:</strong> {userName(selectedReport.reportingUser)}</div>
                <div><strong>Reported user:</strong> {userName(selectedReport.reportedUser)}</div>
                <div><strong>Reason:</strong> {formatLabel(selectedReport.reason)}</div>
                <div><strong>Conversation:</strong> #{selectedReport.conversationId}</div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Additional details</h3>
                <p className="mt-1 whitespace-pre-wrap">{selectedReport.details || "No details provided."}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Recent message context</h3>
                <div className="mt-2 space-y-2">
                  {(selectedReport.messageSnippets || []).length === 0 && (
                    <p className="text-gray-500">No message snippets were available.</p>
                  )}
                  {(selectedReport.messageSnippets || []).map((message) => (
                    <div key={message.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="text-xs text-gray-500">
                        User #{message.senderId} · {formatDate(message.createdAt)}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-gray-800">{message.content}</p>
                    </div>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="mb-1 block font-medium text-gray-900">Moderation status</span>
                <select
                  value={nextStatus}
                  onChange={({ target }) => setNextStatus(target.value)}
                  disabled={updating}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>{formatLabel(option)}</option>
                  ))}
                </select>
              </label>
              <TextField
                label="Moderation notes"
                fullWidth
                multiline
                rows={4}
                value={resolutionNotes}
                onChange={({ target }) => setResolutionNotes(target.value)}
                disabled={updating}
                inputProps={{ maxLength: 2000 }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReport} color="inherit" disabled={updating}>Cancel</Button>
          <Button onClick={saveReview} variant="contained" disabled={updating}>
            {updating ? "Saving..." : "Save Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
